import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Button, Box, CircularProgress,
  Chip, TextField, InputAdornment, Tooltip, useMediaQuery, useTheme,
  Divider, Card, CardContent, CardActions, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Edit, Visibility, Search, PersonAdd,
  CalendarToday, Phone, Email, ArrowUpward, ArrowDownward, UnfoldMore, Clear,
} from '@mui/icons-material';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { format, differenceInDays, isValid } from 'date-fns';
import { Booking, ROOM_INVENTORY, ROOM_TYPES } from '../services/api';
import { APP_ROUTES } from '../constants/routes';
import { BOOKING_STATUS, BOOKING_STATUS_LABEL, STATUS_COLORS } from '../constants/booking';
import { TOAST_SEVERITY } from '../constants/toast';
import {
  GUEST_LIST_DATE_FORMAT,
  GUEST_LIST_FIELD_LABEL,
  GUEST_LIST_MEMBER_FILTER,
  GUEST_LIST_MEMBER_FILTER_LABEL,
  GUEST_LIST_MESSAGE,
  GUEST_LIST_ROOM,
  GUEST_LIST_SORT_KEY,
  GUEST_LIST_SORT_OPTION,
  GUEST_LIST_TABLE_COLUMN,
  GUEST_LIST_TEST_ID,
  GUEST_LIST_TEXT,
  GuestListMemberFilter,
} from '../constants/guestList';
import { useAuthState } from '../store/authHooks';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useToastActions } from '../store/toastHooks';
import {
  guestListActions,
  selectGuestListGuests,
  selectGuestListLoading,
  selectRoomAvailability,
  selectRoomAvailabilityDate,
  selectRoomAvailabilityLoading,
} from '../store/guestListSlice';

const guestTableColumnSx: Record<string, object> = {
  [GUEST_LIST_TABLE_COLUMN.SL_NO]: { width: 42, minWidth: 42, maxWidth: 42, px: 0.5, textAlign: 'center' },
  [GUEST_LIST_TABLE_COLUMN.NAME]: { minWidth: 140, maxWidth: 180 },
  [GUEST_LIST_TABLE_COLUMN.PHONE]: { width: 112, minWidth: 112 },
  [GUEST_LIST_TABLE_COLUMN.CHECKIN]: { width: 104, minWidth: 104 },
  [GUEST_LIST_TABLE_COLUMN.CHECKOUT]: { width: 104, minWidth: 104 },
  [GUEST_LIST_TABLE_COLUMN.DAYS]: { width: 64, minWidth: 64, maxWidth: 64, px: 1, textAlign: 'center' },
  [GUEST_LIST_TABLE_COLUMN.ROOM_DETAILS]: { minWidth: 170, maxWidth: 220 },
  [GUEST_LIST_TABLE_COLUMN.MEMBERS]: { width: 78, minWidth: 78, maxWidth: 78, px: 1, textAlign: 'center' },
  [GUEST_LIST_TABLE_COLUMN.STATUS]: { width: 96, minWidth: 96, maxWidth: 96, pl: 0, pr: 0 },
  [GUEST_LIST_TABLE_COLUMN.ADVANCE]: { width: 104, minWidth: 104, pl: 0 },
  [GUEST_LIST_TABLE_COLUMN.TOTAL]: { width: 112, minWidth: 112 },
  [GUEST_LIST_TABLE_COLUMN.REMARKS]: { width: 120, minWidth: 120, maxWidth: 120 },
};

const guestTableBodyCellSx = { pl: 0, pr: 0 };
const guestTableHeaderCellSx = { pl: 0, pr: 0 };
const selectedSummaryChipSx = {
  boxShadow: '0 5px 14px rgba(25,118,210,0.2)',
  outline: '2px solid rgba(25,118,210,0.28)',
  outlineOffset: 1,
};
const clickableSummaryChipSx = {
  cursor: 'pointer',
  border: '1px solid rgba(15,23,42,0.12)',
  boxShadow: '0 4px 10px rgba(15,23,42,0.12)',
  transform: 'translateY(0)',
  transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease, background-color 140ms ease',
  '&:hover': {
    borderColor: 'primary.main',
    boxShadow: '0 8px 18px rgba(25,118,210,0.18)',
    transform: 'translateY(-1px)',
  },
  '&:active': { transform: 'translateY(0)' },
};

const GuestList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const { canWrite } = useAuthState();
  const { showToast } = useToastActions();

  const todayStr = format(new Date(), GUEST_LIST_DATE_FORMAT.ISO_DATE);
  const returnDate = (location.state as any)?.returnDate || todayStr;

  const guests = useAppSelector(selectGuestListGuests);
  const loading = useAppSelector(selectGuestListLoading);
  const roomAvail = useAppSelector(selectRoomAvailability);
  const roomAvailDate = useAppSelector(selectRoomAvailabilityDate);
  const roomAvailLoading = useAppSelector(selectRoomAvailabilityLoading);
  const [filters, setFilters] = useState({ date: returnDate, name: '', phone: '', email: '', status: '' });
  const [inputValues, setInputValues] = useState({ date: returnDate, name: '', phone: '', email: '', status: '' });
  const [selectedRoomType, setSelectedRoomType] = useState<string>('');
  const [selectedMemberFilters, setSelectedMemberFilters] = useState<GuestListMemberFilter[]>([]);
  const initialDateRef = useRef(returnDate); // capture once — used for initial room avail fetch

  // Clear returnDate from location state after reading it so stale future dates
  // don't re-initialize filters if the component remounts (e.g. browser back).
  useEffect(() => {
    if ((location.state as any)?.returnDate) {
      navigate(APP_ROUTES.HOME, { replace: true, state: null });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: GUEST_LIST_SORT_KEY.CHECKIN_DATE, direction: 'desc' });

  /** Updates the table sort key and toggles direction when the same column is selected again. */
  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  /** Dispatches a saga-backed booking list fetch for the active filters. */
  const fetchGuests = useCallback(async () => {
    const params: any = {};
    if (filters.date) params.date = filters.date;
    if (filters.name) params.name = filters.name;
    if (filters.phone) params.phone = filters.phone;
    if (filters.email) params.email = filters.email;
    if (filters.status) params.status = filters.status;
    dispatch(guestListActions.fetchGuestsRequest(params));
  }, [dispatch, filters]);

  /** Dispatches a saga-backed room availability fetch for the selected date. */
  const fetchRoomAvail = useCallback(async (date: string) => {
    if (!date) return;
    dispatch(guestListActions.fetchRoomAvailabilityRequest(date));
  }, [dispatch]);

  useEffect(() => { fetchGuests(); }, [fetchGuests]);
  useEffect(() => { fetchRoomAvail(initialDateRef.current); }, [fetchRoomAvail]);

  /** Applies entered search fields and warns when the user tries to search without any filter. */
  const handleSearch = useCallback(() => {
    const hasAnyFilter = Boolean(
      inputValues.date ||
      inputValues.name.trim() ||
      inputValues.phone.trim() ||
      inputValues.email.trim() ||
      inputValues.status
    );
    if (!hasAnyFilter) {
      showToast(GUEST_LIST_MESSAGE.SEARCH_FILTER_REQUIRED, TOAST_SEVERITY.WARNING);
      return;
    }
    setFilters({ ...inputValues });
    if (inputValues.date) fetchRoomAvail(inputValues.date);
  }, [fetchRoomAvail, inputValues, showToast]);

  /** Clears booking-date filtering and hides dependent room availability filters. */
  const handleClearDate = useCallback(() => {
    setInputValues(f => ({ ...f, date: '' }));
    dispatch(guestListActions.clearRoomAvailability());
    setSelectedRoomType('');
    setSelectedMemberFilters([]);
  }, [dispatch]);

  /** Formats API date values for display in the list. */
  const fmtDate = useCallback((d?: string) => {
    if (!d) return GUEST_LIST_TEXT.EMPTY_VALUE;
    const dt = new Date(d);
    return isValid(dt) ? format(dt, GUEST_LIST_DATE_FORMAT.DISPLAY_DATE) : GUEST_LIST_TEXT.EMPTY_VALUE;
  }, []);

  /** Calculates booking nights from check-in and check-out dates. */
  const numDays = useCallback((checkin: string, checkout: string) => {
    try { return differenceInDays(new Date(checkout), new Date(checkin)); } catch { return '-'; }
  }, []);

  /** Builds a readable room summary from existing summary data or day-wise allocation. */
  const getRoomSummary = useCallback((booking: Booking) => {
    if (booking.ROOM_SUMMARY) return booking.ROOM_SUMMARY;
    if (!booking.dayBookings?.length) return GUEST_LIST_TEXT.EMPTY_VALUE;
    const allRooms = booking.dayBookings.flatMap(d => d.rooms);
    const types = allRooms.map(r => r.ROOM_TYPE === GUEST_LIST_ROOM.OTHER_ROOM_TYPE ? r.CUSTOM_ROOM_TYPE || GUEST_LIST_ROOM.OTHER_ROOM_TYPE : r.ROOM_TYPE);
    const uniqueTypes = types.filter((t, i) => types.indexOf(t) === i);
    return uniqueTypes.join(', ');
  }, []);

  /** Matches a room summary against an exact room type without matching longer type names by substring. */
  const roomSummaryHasRoomType = useCallback((summary: string, roomType: string) => {
    let searchableSummary = summary.toLowerCase();
    const selectedType = roomType.toLowerCase();
    ROOM_TYPES
      .filter(type => type !== roomType && type.toLowerCase().includes(selectedType))
      .sort((a, b) => b.length - a.length)
      .forEach(type => {
        searchableSummary = searchableSummary.replaceAll(type.toLowerCase(), '');
      });
    return searchableSummary.includes(selectedType);
  }, []);

  /** Returns whether a booking matches the selected room-type filter. */
  const bookingHasRoomType = useCallback((booking: Booking, roomType: string) => {
    if (!roomType) return true;
    const dayRooms = booking.dayBookings?.flatMap(day => day.rooms) || [];
    if (dayRooms.length > 0) {
      return dayRooms.some(room => {
        const type = room.ROOM_TYPE === GUEST_LIST_ROOM.OTHER_ROOM_TYPE ? room.CUSTOM_ROOM_TYPE || GUEST_LIST_ROOM.OTHER_ROOM_TYPE : room.ROOM_TYPE;
        return type === roomType;
      });
    }
    return roomSummaryHasRoomType(booking.ROOM_SUMMARY || '', roomType);
  }, [roomSummaryHasRoomType]);

  /** Calculates total members including adults, children, and drivers for list display. */
  const getTotalMembers = useCallback((booking: Booking) =>
    (booking.NO_ADULTS || 0) + (booking.NO_CHILDREN || 0) + (booking.NO_DRIVERS || 0), []);

  /** Toggles children/driver filters used by the room availability summary chips. */
  const toggleMemberFilter = useCallback((filter: GuestListMemberFilter) => {
    setSelectedMemberFilters(current =>
      current.includes(filter)
        ? current.filter(item => item !== filter)
        : [...current, filter]
    );
  }, []);

  /** Returns whether a booking matches the selected children/driver filters. */
  const bookingMatchesMemberFilters = useCallback((booking: Booking) => {
    if (selectedMemberFilters.length === 0) return true;
    return selectedMemberFilters.some(filter => {
      if (filter === GUEST_LIST_MEMBER_FILTER.CHILDREN) return (booking.NO_CHILDREN || 0) > 0;
      if (filter === GUEST_LIST_MEMBER_FILTER.DRIVERS) return (booking.NO_DRIVERS || 0) > 0;
      return true;
    });
  }, [selectedMemberFilters]);

  const visibleGuests = useMemo(
    () => guests.filter(booking => bookingHasRoomType(booking, selectedRoomType) && bookingMatchesMemberFilters(booking)),
    [bookingHasRoomType, bookingMatchesMemberFilters, guests, selectedRoomType]
  );

  const sortedGuests = useMemo(() => [...visibleGuests].sort((a, b) => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    switch (sortConfig.key) {
      case GUEST_LIST_SORT_KEY.GUEST_NAME: return dir * (a.GUEST_NAME || '').localeCompare(b.GUEST_NAME || '');
      case GUEST_LIST_SORT_KEY.CHECKIN_DATE: return dir * ((a.CHECKIN_DATE || '') < (b.CHECKIN_DATE || '') ? -1 : 1);
      case GUEST_LIST_SORT_KEY.CHECKOUT_DATE: return dir * ((a.CHECKOUT_DATE || '') < (b.CHECKOUT_DATE || '') ? -1 : 1);
      case GUEST_LIST_SORT_KEY.DAYS: return dir * (differenceInDays(new Date(a.CHECKOUT_DATE), new Date(a.CHECKIN_DATE)) - differenceInDays(new Date(b.CHECKOUT_DATE), new Date(b.CHECKIN_DATE)));
      case GUEST_LIST_SORT_KEY.MEMBERS: return dir * (getTotalMembers(a) - getTotalMembers(b));
      case GUEST_LIST_SORT_KEY.STATUS: return dir * (a.STATUS || '').localeCompare(b.STATUS || '');
      case GUEST_LIST_SORT_KEY.ADVANCE_AMOUNT: return dir * ((a.ADVANCE_AMOUNT || 0) - (b.ADVANCE_AMOUNT || 0));
      case GUEST_LIST_SORT_KEY.TOTAL_AMOUNT: return dir * ((a.TOTAL_AMOUNT || 0) - (b.TOTAL_AMOUNT || 0));
      default: return 0;
    }
  }), [getTotalMembers, sortConfig.direction, sortConfig.key, visibleGuests]);

  const activeGuestTotals = useMemo(() => {
    const active = visibleGuests.filter(g => g.STATUS?.toLowerCase() !== BOOKING_STATUS.CANCELLED);
    const adults = active.reduce((s, g) => s + (g.NO_ADULTS || 0), 0);
    const children = active.reduce((s, g) => s + (g.NO_CHILDREN || 0), 0);
    const drivers = active.reduce((s, g) => s + (g.NO_DRIVERS || 0), 0);
    return { adults, children, drivers, total: adults + children + drivers };
  }, [visibleGuests]);

  const visibleRoomTypes = useMemo(() => ROOM_TYPES.filter(t => t !== GUEST_LIST_ROOM.OTHER_ROOM_TYPE), []);
  const stickyActionsHeaderSx = {
    position: 'sticky',
    right: 0,
    zIndex: 4,
    minWidth: 92,
    boxShadow: '-8px 0 12px -12px rgba(0,0,0,0.45)',
  };
  /** Keeps row action buttons pinned while the desktop table scrolls horizontally. */
  const stickyActionsCellSx = (backgroundColor: string) => ({
    position: 'sticky',
    right: 0,
    zIndex: 2,
    minWidth: 92,
    bgcolor: backgroundColor,
    boxShadow: '-8px 0 12px -12px rgba(0,0,0,0.35)',
  });

  return (
    <Box sx={{ background: 'linear-gradient(180deg,#eaf4fb 0%,#f8fbff 100%)', minHeight: '100vh', pb: 6 }}>

      <Container maxWidth="xl" sx={{ pt: 3 }}>
        {/* Search Bar */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark' }}>
            <Search sx={{ verticalAlign: 'middle', mr: 1 }} />
            {GUEST_LIST_TEXT.SEARCH_TITLE}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr auto' },
              alignItems: 'center',
            }}
          >
            <TextField
              label={GUEST_LIST_FIELD_LABEL.BOOKING_DATE}
              type="date"
              size="small"
              value={inputValues.date}
              onChange={e => {
                const val = e.target.value;
                setInputValues(f => ({ ...f, date: val }));
                setFilters(f => ({ ...f, date: val }));
                if (val) fetchRoomAvail(val);
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><CalendarToday fontSize="small" /></InputAdornment>,
                endAdornment: inputValues.date ? (
                  <InputAdornment position="end">
                    <Tooltip title={GUEST_LIST_TEXT.CLEAR_DATE}>
                      <IconButton size="small" edge="end" onClick={handleClearDate}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <TextField
              label={GUEST_LIST_FIELD_LABEL.GUEST_NAME}
              size="small"
              value={inputValues.name}
              onChange={e => setInputValues(f => ({ ...f, name: e.target.value }))}
              inputProps={{ 'data-testid': GUEST_LIST_TEST_ID.SEARCH_NAME }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            />
            <TextField
              label={GUEST_LIST_FIELD_LABEL.PHONE}
              size="small"
              value={inputValues.phone}
              onChange={e => setInputValues(f => ({ ...f, phone: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start"><Phone fontSize="small" /></InputAdornment> }}
            />
            <TextField
              label={GUEST_LIST_FIELD_LABEL.EMAIL}
              size="small"
              value={inputValues.email}
              onChange={e => setInputValues(f => ({ ...f, email: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start"><Email fontSize="small" /></InputAdornment> }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{GUEST_LIST_FIELD_LABEL.STATUS}</InputLabel>
              <Select
                label={GUEST_LIST_FIELD_LABEL.STATUS}
                value={inputValues.status}
                onChange={e => setInputValues(f => ({ ...f, status: e.target.value }))}
              >
                <MenuItem value="">{GUEST_LIST_TEXT.ALL}</MenuItem>
                <MenuItem value={BOOKING_STATUS.ACTIVE}>{BOOKING_STATUS_LABEL[BOOKING_STATUS.ACTIVE]}</MenuItem>
                <MenuItem value={BOOKING_STATUS.PENDING}>{BOOKING_STATUS_LABEL[BOOKING_STATUS.PENDING]}</MenuItem>
                <MenuItem value={BOOKING_STATUS.RESCHEDULED}>{BOOKING_STATUS_LABEL[BOOKING_STATUS.RESCHEDULED]}</MenuItem>
                <MenuItem value={BOOKING_STATUS.CANCELLED}>{BOOKING_STATUS_LABEL[BOOKING_STATUS.CANCELLED]}</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<Search />}
              data-testid={GUEST_LIST_TEST_ID.SEARCH_BUTTON}
              sx={{ height: 40, whiteSpace: 'nowrap' }}
            >
              {GUEST_LIST_TEXT.SEARCH_BUTTON}
            </Button>
          </Box>
        </Paper>

        {/* Availability filters */}
        {inputValues.date && roomAvail && roomAvailDate === inputValues.date && (
          <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 700 }}>
                {GUEST_LIST_TEXT.ROOM_AVAILABILITY_TITLE} — {format(new Date(`${roomAvailDate}T00:00:00`), GUEST_LIST_DATE_FORMAT.DISPLAY_DATE)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={<><strong>{activeGuestTotals.total}</strong> {GUEST_LIST_TEXT.TOTAL_GUESTS}</>} size="small" sx={{ bgcolor: '#e8eaf6', color: '#3949ab', fontWeight: 600 }} />
                <Chip label={<>{GUEST_LIST_TEXT.ADULTS}: <strong>{activeGuestTotals.adults}</strong></>} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                <Chip
                  label={<>{GUEST_LIST_TEXT.CHILDREN}: <strong>{activeGuestTotals.children}</strong></>}
                  size="small"
                  clickable
                  data-testid={GUEST_LIST_TEST_ID.MEMBER_FILTER_CHILDREN}
                  onClick={() => toggleMemberFilter(GUEST_LIST_MEMBER_FILTER.CHILDREN)}
                  sx={{
                    bgcolor: selectedMemberFilters.includes(GUEST_LIST_MEMBER_FILTER.CHILDREN) ? '#bbdefb' : '#fce4ec',
                    color: selectedMemberFilters.includes(GUEST_LIST_MEMBER_FILTER.CHILDREN) ? '#0d47a1' : '#c62828',
                    fontWeight: selectedMemberFilters.includes(GUEST_LIST_MEMBER_FILTER.CHILDREN) ? 700 : 500,
                    ...clickableSummaryChipSx,
                    ...(selectedMemberFilters.includes(GUEST_LIST_MEMBER_FILTER.CHILDREN) ? selectedSummaryChipSx : {}),
                  }}
                />
                <Chip
                  label={<>{GUEST_LIST_TEXT.DRIVERS}: <strong>{activeGuestTotals.drivers}</strong></>}
                  size="small"
                  clickable
                  data-testid={GUEST_LIST_TEST_ID.MEMBER_FILTER_DRIVERS}
                  onClick={() => toggleMemberFilter(GUEST_LIST_MEMBER_FILTER.DRIVERS)}
                  sx={{
                    bgcolor: selectedMemberFilters.includes(GUEST_LIST_MEMBER_FILTER.DRIVERS) ? '#bbdefb' : '#e8f5e9',
                    color: selectedMemberFilters.includes(GUEST_LIST_MEMBER_FILTER.DRIVERS) ? '#0d47a1' : '#2e7d32',
                    fontWeight: selectedMemberFilters.includes(GUEST_LIST_MEMBER_FILTER.DRIVERS) ? 700 : 500,
                    ...clickableSummaryChipSx,
                    ...(selectedMemberFilters.includes(GUEST_LIST_MEMBER_FILTER.DRIVERS) ? selectedSummaryChipSx : {}),
                  }}
                />
              </Box>
            </Box>
            {roomAvailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={28} /></Box>
            ) : (
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)', lg: 'repeat(8,1fr)' } }}>
                {visibleRoomTypes.map(type => {
                  const total = (ROOM_INVENTORY[type] || []).length;
                  const available = (roomAvail[type] || []).length;
                  const allocated = total - available;
                  const isFull = available === 0 && total > 0;
                  const isEmpty = allocated === 0;
                  const isSelected = selectedRoomType === type;
                  return (
                    <Box
                      key={type}
                      role="button"
                      aria-label={GUEST_LIST_TEXT.ROOM_TYPE_FILTER(type)}
                      data-testid={GUEST_LIST_TEST_ID.ROOM_TYPE_FILTER(type)}
                      tabIndex={0}
                      onClick={() => setSelectedRoomType(current => current === type ? '' : type)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedRoomType(current => current === type ? '' : type);
                        }
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        textAlign: 'center',
                        border: '1px solid',
                        borderColor: isSelected ? 'primary.main' : isFull ? 'error.light' : isEmpty ? 'success.light' : 'warning.light',
                        bgcolor: isSelected ? '#e3f2fd' : isFull ? '#fff5f5' : isEmpty ? '#f0fff4' : '#fffbf0',
                        boxShadow: isSelected
                          ? '0 14px 30px rgba(25,118,210,0.22)'
                          : '0 8px 18px rgba(15,23,42,0.12)',
                        cursor: 'pointer',
                        outline: 'none',
                        transform: 'translateY(0)',
                        transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease, background-color 140ms ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: '0 12px 26px rgba(25,118,210,0.22)',
                          transform: 'translateY(-2px)',
                        },
                        '&:active': { transform: 'translateY(-1px)' },
                        '&:focus-visible': {
                          borderColor: 'primary.main',
                          boxShadow: '0 0 0 3px rgba(25,118,210,0.22), 0 10px 24px rgba(25,118,210,0.16)',
                        },
                      }}>
                      <Typography fontSize="0.75rem" fontWeight={700} color="primary.dark" sx={{ mb: 0.5, lineHeight: 1.2 }}>{type}</Typography>
                      <Typography fontSize="0.65rem" color="text.secondary" sx={{ mb: 0.5 }}>{GUEST_LIST_TEXT.TOTAL}: {total}</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                        <Box>
                          <Typography fontSize="1.1rem" fontWeight={800} color="error.main">{allocated}</Typography>
                          <Typography fontSize="0.65rem" color="text.secondary">{GUEST_LIST_TEXT.ALLOCATED}</Typography>
                        </Box>
                        <Typography fontSize="1rem" color="text.disabled" sx={{ alignSelf: 'center' }}>|</Typography>
                        <Box>
                          <Typography fontSize="1.1rem" fontWeight={800} color="success.main">{available}</Typography>
                          <Typography fontSize="0.65rem" color="text.secondary">{GUEST_LIST_TEXT.AVAILABLE}</Typography>
                        </Box>
                      </Box>
                      {isFull && <Chip label={GUEST_LIST_TEXT.FULL} color="error" size="small" sx={{ mt: 0.5, fontSize: '0.65rem', height: 18 }} />}
                      {isEmpty && total > 0 && <Chip label={GUEST_LIST_TEXT.FREE} color="success" size="small" sx={{ mt: 0.5, fontSize: '0.65rem', height: 18 }} />}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        )}

        {/* Action Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6" color="primary.dark">
            {GUEST_LIST_TEXT.BOOKING_FOUND(visibleGuests.length)}
            {selectedRoomType && (
              <Typography component="span" color="text.secondary" fontSize="0.9rem" sx={{ ml: 1 }}>
                {GUEST_LIST_TEXT.FILTER_FOR_ROOM(selectedRoomType)}
              </Typography>
            )}
            {selectedMemberFilters.length > 0 && (
              <Typography component="span" color="text.secondary" fontSize="0.9rem" sx={{ ml: 1 }}>
                {GUEST_LIST_TEXT.FILTER_WITH_MEMBERS(selectedMemberFilters.map(filter => GUEST_LIST_MEMBER_FILTER_LABEL[filter]).join(' or '))}
              </Typography>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{GUEST_LIST_TEXT.SORT_BY}</InputLabel>
              <Select
                label={GUEST_LIST_TEXT.SORT_BY}
                value={sortConfig.key}
                onChange={e => setSortConfig(prev => ({ ...prev, key: e.target.value }))}
              >
                {GUEST_LIST_SORT_OPTION.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() => setSortConfig(prev => ({ ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }))}
              title={sortConfig.direction === 'asc' ? GUEST_LIST_TEXT.ASCENDING : GUEST_LIST_TEXT.DESCENDING}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              {sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
            </IconButton>
            {canWrite && (
              <Button
                variant="contained"
                color="secondary"
                component={Link}
                to={APP_ROUTES.ADD_BOOKING}
                startIcon={<PersonAdd />}
                sx={{ background: 'linear-gradient(90deg,#52b788,#74c69d)', color: '#fff' }}
              >
                {GUEST_LIST_TEXT.NEW_BOOKING}
              </Button>
            )}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress size={48} />
          </Box>
        ) : visibleGuests.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <Typography color="text.secondary" fontSize="1.1rem">
              {selectedRoomType || selectedMemberFilters.length > 0
                ? GUEST_LIST_MESSAGE.NO_BOOKINGS_FOR_FILTERS(
                  selectedRoomType,
                  selectedMemberFilters.map(filter => GUEST_LIST_MEMBER_FILTER_LABEL[filter]).join(' or ')
                )
                : GUEST_LIST_MESSAGE.NO_BOOKINGS_SELECTED_FILTERS}
            </Typography>
          </Paper>
        ) : isMobile ? (
          // Mobile card view
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sortedGuests.map(g => (
              <Card key={g.BOOKINGID} sx={{ borderRadius: 3, borderLeft: `4px solid ${theme.palette[STATUS_COLORS[g.STATUS] as 'success' | 'warning' | 'error' | 'info']?.main || '#ccc'}` }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography fontWeight={700} fontSize="1rem">{g.GUEST_NAME}</Typography>
                    <Chip label={g.STATUS} color={STATUS_COLORS[g.STATUS] || 'default'} size="small" />
                  </Box>
                  <Typography color="text.secondary" fontSize="0.82rem">{g.PHONE} | {g.EMAIL || GUEST_LIST_TEXT.EMPTY_VALUE}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                    <Typography fontSize="0.8rem"><b>{GUEST_LIST_TEXT.CHECKIN}:</b> {fmtDate(g.CHECKIN_DATE)}</Typography>
                    <Typography fontSize="0.8rem"><b>{GUEST_LIST_TEXT.CHECKOUT}:</b> {fmtDate(g.CHECKOUT_DATE)}</Typography>
                    <Typography fontSize="0.8rem"><b>{GUEST_LIST_TEXT.DAYS}:</b> {numDays(g.CHECKIN_DATE, g.CHECKOUT_DATE)}</Typography>
                    <Typography fontSize="0.8rem"><b>{GUEST_LIST_TEXT.MEMBERS}:</b> {getTotalMembers(g)}</Typography>
                    <Typography fontSize="0.8rem"><b>{GUEST_LIST_TEXT.ROOMS}:</b> {getRoomSummary(g)}</Typography>
                    <Typography fontSize="0.8rem"><b>{GUEST_LIST_TEXT.TOTAL}:</b> {GUEST_LIST_TEXT.CURRENCY} {g.TOTAL_AMOUNT?.toFixed(2)}</Typography>
                    <Typography fontSize="0.8rem"><b>{GUEST_LIST_TEXT.ADVANCE}:</b> {GUEST_LIST_TEXT.CURRENCY} {g.ADVANCE_AMOUNT?.toFixed(2)}</Typography>
                    <Typography fontSize="0.8rem"><b>{GUEST_LIST_TEXT.REMAINING}:</b> {GUEST_LIST_TEXT.CURRENCY} {((g.TOTAL_AMOUNT || 0) - (g.ADVANCE_AMOUNT || 0)).toFixed(2)}</Typography>
                  </Box>
                  {g.REMARKS && <Typography fontSize="0.8rem" mt={0.5} color="text.secondary"><i>{g.REMARKS}</i></Typography>}
                </CardContent>
                <CardActions sx={{ pt: 0, px: 2 }}>
                  <Tooltip title={GUEST_LIST_TEXT.VIEW}><IconButton size="small" color="primary" data-testid={GUEST_LIST_TEST_ID.BUTTON_VIEW} onClick={() => navigate(APP_ROUTES.VIEW_BOOKING(g.BOOKINGID!), { state: { returnDate: filters.date } })}><Visibility /></IconButton></Tooltip>
                  {canWrite && <Tooltip title={GUEST_LIST_TEXT.EDIT}><IconButton size="small" color="warning" data-testid={GUEST_LIST_TEST_ID.BUTTON_EDIT} onClick={() => navigate(APP_ROUTES.EDIT_BOOKING(g.BOOKINGID!), { state: { returnDate: filters.date } })}><Edit /></IconButton></Tooltip>}

                </CardActions>
              </Card>
            ))}
          </Box>
        ) : (
          // Desktop table view
          <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {([
                    { label: GUEST_LIST_TABLE_COLUMN.SL_NO, key: null },
                    { label: GUEST_LIST_TABLE_COLUMN.NAME, key: GUEST_LIST_SORT_KEY.GUEST_NAME },
                    { label: GUEST_LIST_TABLE_COLUMN.PHONE, key: null },
                    { label: GUEST_LIST_TABLE_COLUMN.CHECKIN, key: GUEST_LIST_SORT_KEY.CHECKIN_DATE },
                    { label: GUEST_LIST_TABLE_COLUMN.CHECKOUT, key: GUEST_LIST_SORT_KEY.CHECKOUT_DATE },
                    { label: GUEST_LIST_TABLE_COLUMN.DAYS, key: GUEST_LIST_SORT_KEY.DAYS },
                    { label: GUEST_LIST_TABLE_COLUMN.ROOM_DETAILS, key: null },
                    { label: GUEST_LIST_TABLE_COLUMN.MEMBERS, key: GUEST_LIST_SORT_KEY.MEMBERS },
                    { label: GUEST_LIST_TABLE_COLUMN.STATUS, key: GUEST_LIST_SORT_KEY.STATUS },
                    { label: GUEST_LIST_TABLE_COLUMN.ADVANCE, key: GUEST_LIST_SORT_KEY.ADVANCE_AMOUNT },
                    { label: GUEST_LIST_TABLE_COLUMN.TOTAL, key: GUEST_LIST_SORT_KEY.TOTAL_AMOUNT },
                    { label: GUEST_LIST_TABLE_COLUMN.REMARKS, key: null },
                    { label: GUEST_LIST_TABLE_COLUMN.ACTIONS, key: null },
                  ] as { label: string; key: string | null }[]).map(({ label, key }) => (
                    <TableCell
                      key={label}
                      sx={{
                        color: '#fff',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        bgcolor: 'primary.main',
                        ...guestTableColumnSx[label],
                        ...guestTableHeaderCellSx,
                        ...(label === GUEST_LIST_TABLE_COLUMN.ACTIONS ? stickyActionsHeaderSx : {}),
                      }}
                    >
                      {key ? (
                        <TableSortLabel
                          active={sortConfig.key === key}
                          direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort(key)}
                          sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                          IconComponent={sortConfig.key === key ? undefined : UnfoldMore}
                        >
                          {label}
                        </TableSortLabel>
                      ) : label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedGuests.map((g, idx) => (
                  <TableRow
                    key={g.BOOKINGID}
                    sx={{
                      bgcolor: idx % 2 === 0 ? '#f0f7ff' : '#fff',
                      '&:hover': { bgcolor: '#d0eaff' },
                      '&:hover td:last-of-type': { bgcolor: '#d0eaff' },
                    }}
                  >
                    <TableCell align="center" sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.SL_NO], ...guestTableBodyCellSx }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.NAME], ...guestTableBodyCellSx, fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>
                      <Tooltip title={g.GUEST_NAME} placement="top" arrow><span>{g.GUEST_NAME}</span></Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.PHONE], ...guestTableBodyCellSx, whiteSpace: 'nowrap' }}>{g.PHONE}</TableCell>
                    <TableCell sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.CHECKIN], ...guestTableBodyCellSx, whiteSpace: 'nowrap' }}>{fmtDate(g.CHECKIN_DATE)}</TableCell>
                    <TableCell sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.CHECKOUT], ...guestTableBodyCellSx, whiteSpace: 'nowrap' }}>{fmtDate(g.CHECKOUT_DATE)}</TableCell>
                    <TableCell align="center" sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.DAYS], ...guestTableBodyCellSx }}>{numDays(g.CHECKIN_DATE, g.CHECKOUT_DATE)}</TableCell>
                    <TableCell sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.ROOM_DETAILS], ...guestTableBodyCellSx, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>
                      <Tooltip title={getRoomSummary(g)} placement="top" arrow><span>{getRoomSummary(g)}</span></Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.MEMBERS], ...guestTableBodyCellSx }}>{getTotalMembers(g)}</TableCell>
                    <TableCell sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.STATUS], ...guestTableBodyCellSx }}><Chip label={g.STATUS} color={STATUS_COLORS[g.STATUS] || 'default'} size="small" /></TableCell>
                    <TableCell sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.ADVANCE], ...guestTableBodyCellSx, whiteSpace: 'nowrap' }}>{GUEST_LIST_TEXT.CURRENCY} {g.ADVANCE_AMOUNT?.toFixed(2)}</TableCell>
                    <TableCell sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.TOTAL], ...guestTableBodyCellSx, whiteSpace: 'nowrap' }}>{GUEST_LIST_TEXT.CURRENCY} {g.TOTAL_AMOUNT?.toFixed(2)}</TableCell>
                    <TableCell sx={{ ...guestTableColumnSx[GUEST_LIST_TABLE_COLUMN.REMARKS], ...guestTableBodyCellSx, pr: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={g.REMARKS || ''} placement="top" arrow disableHoverListener={!g.REMARKS}><span>{g.REMARKS || GUEST_LIST_TEXT.EMPTY_VALUE}</span></Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...stickyActionsCellSx(idx % 2 === 0 ? '#f0f7ff' : '#fff'), ...guestTableBodyCellSx }}>
                      <Box sx={{ display: 'flex', gap: 0.3 }}>
                        <Tooltip title={GUEST_LIST_TEXT.VIEW}><IconButton size="small" color="primary" data-testid={GUEST_LIST_TEST_ID.BUTTON_VIEW} onClick={() => navigate(APP_ROUTES.VIEW_BOOKING(g.BOOKINGID!), { state: { returnDate: filters.date } })}><Visibility fontSize="small" /></IconButton></Tooltip>
                        {canWrite && <Tooltip title={GUEST_LIST_TEXT.EDIT}><IconButton size="small" color="warning" data-testid={GUEST_LIST_TEST_ID.BUTTON_EDIT} onClick={() => navigate(APP_ROUTES.EDIT_BOOKING(g.BOOKINGID!), { state: { returnDate: filters.date } })}><Edit fontSize="small" /></IconButton></Tooltip>}

                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Box>
  );
};

export default GuestList;
